import { h } from '@omni-torn/ui';
import {
  BackupChecker,
  backupFileName,
  DATA_STREAM_NAMES,
  DATA_STREAMS,
  dayRange,
  isOwnStream,
  mergeLists,
  mergePricelist,
  mergeSettings,
  OWN_STREAM_NAMES,
  type BackupCheck,
  type BackupPricelist,
  type BackupSettingsShape,
  type DataStream,
  type PricelistMine,
  type Prefer,
} from '@omni-torn/shared';
import { server, session } from '../session';
import { box, downloadBlob, num, problem, table, tct } from '../ui';

/**
 * Account → Data (website roadmap S40; server routes/data.ts, shared data-tools.ts): what Omni-Torn keeps for you, a backup
 * file of all of it, restoring one (checked first, merged with a preview — nothing on the server is overwritten unless you
 * pick that), and deleting kept history by range (two clicks). Only your own data, ever; nothing here asks Torn.
 */

interface SummaryRow {
  stream: DataStream;
  count: number;
  oldest: number | null;
  newest: number | null;
  bytes: number;
}

const day = (ms: number | null): string => (ms ? new Date(ms).toISOString().slice(0, 10) : '—');
const size = (b: number): string => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b} B`);
const listName = (k: string): string => (k === 'omnitorn.alertRules' ? 'Alert rules' : k.replace(/^omnitorn\./, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()));

/** The sub-tabs of Account (addresses: /account and /account/data). */
export function accountTabs(current: 'account' | 'data'): HTMLElement {
  return h(
    'nav',
    { class: 'seg', 'aria-label': 'Account' },
    h('a', { href: '/account', 'data-link': true, class: current === 'account' ? 'active' : '', ...(current === 'account' ? { 'aria-current': 'page' } : {}) }, 'Account & sync'),
    h('a', { href: '/account/data', 'data-link': true, class: current === 'data' ? 'active' : '', 'data-tab': 'data', ...(current === 'data' ? { 'aria-current': 'page' } : {}) }, 'Data'),
  );
}

/** A file's lines (gzipped or not), read as they come — a backup of any size. */
async function* fileLines(file: Blob): AsyncGenerator<string> {
  const head = new Uint8Array(await file.slice(0, 2).arrayBuffer());
  let bytes = file.stream() as ReadableStream<Uint8Array>;
  if (head[0] === 0x1f && head[1] === 0x8b) bytes = bytes.pipeThrough(new DecompressionStream('gzip') as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  const reader = bytes.pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>).getReader();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i: number;
    while ((i = buf.indexOf('\n')) >= 0) {
      yield buf.slice(0, i);
      buf = buf.slice(i + 1);
    }
  }
  if (buf.trim()) yield buf;
}

/** Entries split so each upload stays well under the server's body limit (OWN_UPLOAD_BYTES). */
function chunks(entries: Record<string, unknown>, maxBytes = 700_000): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  let cur: Record<string, unknown> = {};
  let n = 0;
  for (const [id, e] of Object.entries(entries)) {
    const b = JSON.stringify(e).length + id.length + 8;
    if (n && n + b > maxBytes) {
      out.push(cur);
      cur = {};
      n = 0;
    }
    cur[id] = e;
    n += b;
  }
  if (Object.keys(cur).length) out.push(cur);
  return out;
}

export async function renderAccountData(view: HTMLElement): Promise<void> {
  const s = session.get()!;
  const ownOn = session.me()?.ownHistory === true;

  // ---- what's kept
  const keptHolder = h('div', {}, h('p', { class: 'muted' }, 'Loading…'));
  const loadKept = async () => {
    try {
      const r = await server<{ streams: SummaryRow[] }>('GET', '/api/me/data');
      keptHolder.replaceChildren(
        table(
          r.streams,
          [
            { label: 'What', get: (x) => DATA_STREAM_NAMES[x.stream] },
            { label: 'Kept', get: (x) => num(x.count), sort: (x) => x.count, cls: 'r' },
            { label: 'Oldest', get: (x) => day(x.oldest), sort: (x) => x.oldest ?? 0 },
            { label: 'Newest', get: (x) => day(x.newest), sort: (x) => x.newest ?? 0 },
            { label: 'Size', get: (x) => size(x.bytes), sort: (x) => x.bytes, cls: 'r' },
          ],
          { csv: 'kept' },
        ),
      );
      keptHolder.setAttribute('data-kept', r.streams.map((x) => `${x.stream}:${x.count}`).join(','));
    } catch (err) {
      keptHolder.replaceChildren(problem(err));
    }
  };

  // ---- backup
  const backupStatus = h('p', { class: 'form-status', role: 'status', 'data-field': 'backup-status' });
  const backupBtn = h('button', { type: 'button', class: 'btn-primary', 'data-action': 'backup' }, 'Download a backup') as HTMLButtonElement;
  backupBtn.addEventListener('click', async () => {
    backupBtn.disabled = true;
    backupStatus.className = 'form-status';
    backupStatus.textContent = 'Making your backup…';
    try {
      // A fresh sign-in first (renews it if it ran out), then the file as the server streams it.
      await server('GET', '/api/me/data');
      const res = await fetch('/api/me/backup', { headers: { authorization: `Bearer ${session.get()?.token ?? ''}` } });
      if (!res.ok || !res.body) {
        const e = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(e?.error?.message ?? `The server answered ${res.status}.`);
      }
      const reader = res.body.getReader();
      const parts: Uint8Array[] = [];
      let got = 0;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        parts.push(value);
        got += value.length;
        backupStatus.textContent = `Making your backup… ${size(got)}`;
      }
      const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? backupFileName(s.playerId, Date.now());
      downloadBlob(name, new Blob(parts as BlobPart[], { type: 'application/gzip' }));
      backupStatus.className = 'form-status ok';
      backupStatus.textContent = `Saved ${name} (${size(got)}). Keep it somewhere safe — it holds your history and lists (never a key).`;
    } catch (err) {
      backupStatus.className = 'form-status bad';
      backupStatus.textContent = (err as Error).message;
    }
    backupBtn.disabled = false;
  });

  // ---- restore
  const fileInput = h('input', { type: 'file', accept: '.gz,.ndjson,.json,application/gzip', 'data-field': 'restore-file', 'aria-label': 'Backup file' }) as HTMLInputElement;
  const preview = h('div', { class: 'data-preview', 'data-field': 'restore-preview' });
  const restoreStatus = h('p', { class: 'form-status', role: 'status', 'data-field': 'restore-status' });
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    preview.replaceChildren();
    restoreStatus.className = 'form-status';
    restoreStatus.textContent = '';
    if (!file) return;
    restoreStatus.textContent = 'Checking the file…';
    let check: BackupCheck;
    try {
      const c = new BackupChecker(s.playerId, Date.now());
      let n = 0;
      for await (const line of fileLines(file)) {
        if (!c.feed(line)) break;
        if (++n % 200 === 0) restoreStatus.textContent = `Checking the file… ${num(n)} lines`;
      }
      check = c.finish();
    } catch (err) {
      restoreStatus.className = 'form-status bad';
      restoreStatus.textContent = `Couldn’t read that file (${(err as Error).message}). Pick the .ndjson.gz file a backup saved.`;
      return;
    }
    if (check.problem) {
      restoreStatus.className = 'form-status bad';
      restoreStatus.textContent = `Not restored: ${check.problem}`;
      return;
    }
    restoreStatus.textContent = '';
    await drawPreview(file, check);
  });

  const drawPreview = async (file: File, check: BackupCheck) => {
    const made = check.header?.createdAt ?? 0;
    // What the server has now, to show what a restore would change.
    const [setNow, listsNow, plNow] = await Promise.all([
      server<{ settings: BackupSettingsShape | null; updatedAt: number | null }>('GET', '/api/me/settings').catch(() => null),
      server<{ lists: Record<string, unknown> | null; updatedAt: number | null }>('GET', '/api/me/lists').catch(() => null),
      server<PricelistMine>('GET', '/api/pricelists/mine').catch(() => null),
    ]);
    // History goes back only from a backup this server made for you (its signed header); settings and lists are yours to put back either way.
    const signed = (await server<{ signed: boolean }>('POST', '/api/me/backup/check', { header: check.header }).catch(() => null))?.signed === true;
    const choice = (field: string, options: Array<[string, string]>) => h('select', { class: 'data-choice', 'data-field': field, 'aria-label': field }, ...options.map(([v, t]) => h('option', { value: v }, t))) as HTMLSelectElement;
    const mergeChoices: Array<[string, string]> = [
      ['server', 'Add what’s missing — keep what’s here where they differ'],
      ['file', 'Add what’s missing — use the backup’s where they differ'],
      ['skip', 'Leave as it is'],
    ];
    const parts: HTMLElement[] = [h('p', {}, h('strong', {}, `Backup of ${check.header?.name || 'you'} [${check.header?.playerId}]`), `, made ${made ? `${day(made)} ${tct(made)} TCT` : 'at an unknown time'}.`)];

    // History in the file.
    const tallies = DATA_STREAMS.map((x) => ({ stream: x, ...check.tally[x] }));
    parts.push(
      h('h3', {}, 'History in the file'),
      table(tallies, [
        { label: 'What', get: (x) => DATA_STREAM_NAMES[x.stream] },
        { label: 'In the file', get: (x) => num(x.count), cls: 'r' },
        { label: 'Oldest', get: (x) => day(x.oldest) },
        { label: 'Newest', get: (x) => day(x.newest) },
      ], { csv: false }),
    );
    const historyBox = h('input', { type: 'checkbox', checked: signed, disabled: !signed, 'data-field': 'restore-history' }) as HTMLInputElement;
    parts.push(...([
      h('label', { class: 'switch' }, historyBox, ' Put back history that isn’t kept here now (what’s kept stays as it is)'),
      !signed ? h('p', { class: 'notice small', 'data-preview': 'unsigned' }, 'This file wasn’t made by this Omni-Torn server for you (or it was changed), so its history can’t be put back. Your settings, lists and pricelist still can.') : null,
      !ownOn ? h('p', { class: 'notice small' }, 'Your activity log, fights and races are kept on the server from Omni-Torn’s terms version 19 — agree to the current terms to put them back.') : null,
      session.me()?.prefs.history === false ? h('p', { class: 'notice small' }, 'Your history is off (Account → History), so stats history and company days can’t be put back until you switch it on.') : null,
    ].filter(Boolean) as HTMLElement[]));

    // Settings.
    let setSel: HTMLSelectElement | null = null;
    if (check.settings) {
      const m = mergeSettings(setNow?.settings ?? null, check.settings.settings);
      setSel = choice('restore-settings', mergeChoices);
      parts.push(...([
        h('h3', {}, 'Settings'),
        h('p', { 'data-preview': 'settings' }, `${num(m.summary.added)} to add, ${num(m.summary.differ.length)} different here, ${num(m.summary.same)} the same.`, setNow?.updatedAt && setNow.updatedAt > made ? ' Your settings here changed after this backup was made.' : ''),
        m.summary.differ.length ? h('details', {}, h('summary', {}, 'Which differ'), h('ul', {}, ...m.summary.differ.slice(0, 40).map((p) => h('li', { class: 'small' }, p)))) : null,
        setSel,
      ].filter(Boolean) as HTMLElement[]));
    }
    // Lists (alert rules among them).
    let listSel: HTMLSelectElement | null = null;
    if (check.lists) {
      const m = mergeLists(listsNow?.lists ?? null, check.lists.lists);
      listSel = choice('restore-lists', mergeChoices);
      parts.push(...([
        h('h3', {}, 'Lists, notes and alert rules'),
        table(m.lists, [
          { label: 'List', get: (x) => listName(x.key) },
          { label: 'What happens', get: (x) => ({ add: 'added', same: 'already the same', merged: 'entries added', differ: 'entries added; some differ' })[x.action] },
          { label: 'New', get: (x) => num(x.added), cls: 'r' },
          { label: 'Differ', get: (x) => num(x.differ), cls: 'r' },
        ], { csv: false, empty: 'No lists in the file.' }),
        listsNow?.updatedAt && listsNow.updatedAt > made ? h('p', { class: 'muted small' }, 'Your lists here changed after this backup was made.') : null,
        listSel,
      ].filter(Boolean) as HTMLElement[]));
    }
    // The pricelist.
    let plSel: HTMLSelectElement | null = null;
    const plServer = plNow?.list ? { open: plNow.list.open, about: plNow.list.about, items: plNow.list.items.map((i) => ({ itemId: i.itemId, mode: i.mode, value: i.value, note: i.note })) } : null;
    if (check.pricelist) {
      const m = mergePricelist(plServer, check.pricelist);
      plSel = choice('restore-pricelist', mergeChoices);
      parts.push(
        h('h3', {}, 'Your pricelist'),
        h('p', { 'data-preview': 'pricelist' }, `${num(m.summary.added)} item${m.summary.added === 1 ? '' : 's'} to add, ${num(m.summary.differ.length)} priced differently here, ${num(m.summary.same)} the same.`, plNow && !plNow.mayStore ? ` Keeping a pricelist needs Omni-Torn’s terms version ${plNow.termsNeeded}.` : ''),
        plSel,
      );
    }
    const go = h('button', { type: 'button', class: 'btn-primary', 'data-action': 'restore' }, 'Restore') as HTMLButtonElement;
    parts.push(h('p', {}, go));
    preview.replaceChildren(...parts);
    go.addEventListener('click', async () => {
      go.disabled = true;
      fileInput.disabled = true;
      restoreStatus.className = 'form-status';
      const said: string[] = [];
      try {
        const prefer = (sel: HTMLSelectElement | null): Prefer | null => (sel && sel.value !== 'skip' ? (sel.value as Prefer) : null);
        const device = 'Backup restore (website)';
        // Settings and lists: one save each, from the copy just read (if another device saved since, the server says so).
        const sp = prefer(setSel);
        if (check.settings && sp) {
          const m = mergeSettings(setNow?.settings ?? null, check.settings.settings, sp);
          if (m.summary.added || (sp === 'file' && m.summary.differ.length)) {
            const r = await server<{ applied: boolean }>('PUT', '/api/me/settings', { settings: m.merged, updatedAt: Date.now(), device, base: setNow?.updatedAt ?? null });
            said.push(r.applied ? 'settings put back' : 'settings not changed — another device saved them just now; check the file again');
          } else said.push('settings already as in the backup');
        }
        const lp = prefer(listSel);
        if (check.lists && lp) {
          const m = mergeLists(listsNow?.lists ?? null, check.lists.lists, lp);
          if (m.lists.some((x) => x.added || (lp === 'file' && x.differ))) {
            const r = await server<{ applied: boolean }>('PUT', '/api/me/lists', { lists: m.merged, updatedAt: Date.now(), device, base: listsNow?.updatedAt ?? null });
            said.push(r.applied ? 'lists put back' : 'lists not changed — another device saved them just now; check the file again');
          } else said.push('lists already as in the backup');
        }
        const pp = prefer(plSel);
        if (check.pricelist && pp) {
          const m = mergePricelist(plServer, check.pricelist as BackupPricelist, pp);
          if (m.summary.added || (pp === 'file' && m.summary.differ.length) || !plServer) {
            await server('PUT', '/api/pricelists/mine', m.merged)
              .then(() => said.push('pricelist put back'))
              .catch((err: Error) => said.push(`pricelist not put back (${err.message})`));
          } else said.push('pricelist already as in the backup');
        }
        // History: a second read of the file, one batch at a time, each checked by the server like any upload.
        if (historyBox.checked) {
          const tot: Record<DataStream, { added: number; known: number; refused: number }> = Object.fromEntries(DATA_STREAMS.map((x) => [x, { added: 0, known: 0, refused: 0 }])) as Record<DataStream, { added: number; known: number; refused: number }>;
          const notes = new Set<string>();
          let sent = 0;
          const all = DATA_STREAMS.reduce((a, x) => a + check.tally[x].count, 0);
          for await (const text of fileLines(file)) {
            if (!text.trim()) continue;
            const line = JSON.parse(text) as { type: string; items?: unknown[]; entries?: Record<string, unknown> };
            if ((line.type === 'snapshots' || line.type === 'company') && line.items) {
              const r = await server<{ added: number; known: number; refused: number; message?: string }>('POST', `/api/me/restore/${line.type}`, { header: check.header, items: line.items });
              tot[line.type].added += r.added;
              tot[line.type].known += r.known;
              tot[line.type].refused += r.refused;
              if (r.message) notes.add(r.message);
              sent += line.items.length;
            } else if (isOwnStream(line.type) && line.entries) {
              for (const part of chunks(line.entries)) {
                const r = await server<{ added: number; known: number; refused: number; message?: string }>('POST', `/api/me/restore/${line.type}`, { header: check.header, entries: part });
                tot[line.type].added += r.added;
                tot[line.type].known += r.known;
                tot[line.type].refused += r.refused;
                if (r.message) notes.add(r.message);
                sent += Object.keys(part).length;
              }
            } else continue;
            restoreStatus.textContent = `Putting back your history… ${num(sent)} of ${num(all)}`;
          }
          for (const x of DATA_STREAMS) {
            const t = tot[x];
            if (!t.added && !t.known && !t.refused) continue;
            const name = isOwnStream(x) ? OWN_STREAM_NAMES[x] : x === 'snapshots' ? 'stats snapshots' : 'company days';
            said.push(`${name}: ${num(t.added)} put back${t.known ? `, ${num(t.known)} already kept` : ''}${t.refused ? `, ${num(t.refused)} not taken` : ''}`);
          }
          if (notes.size) said.push([...notes].join(' '));
        }
        restoreStatus.className = 'form-status ok';
        restoreStatus.textContent = `Done — ${said.join('; ') || 'nothing to change'}.`;
        void loadKept();
      } catch (err) {
        restoreStatus.className = 'form-status bad';
        restoreStatus.textContent = `Stopped: ${(err as Error).message}${said.length ? ` (done before that: ${said.join('; ')})` : ''}`;
      }
      go.disabled = false;
      fileInput.disabled = false;
    });
  };

  // ---- delete by range
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
  const delStream = h('select', { 'data-field': 'delete-stream' }, ...DATA_STREAMS.map((x) => h('option', { value: x }, DATA_STREAM_NAMES[x]))) as HTMLSelectElement;
  const delFrom = h('input', { type: 'date', value: monthAgo, max: today, 'data-field': 'delete-from' }) as HTMLInputElement;
  const delTo = h('input', { type: 'date', value: today, max: today, 'data-field': 'delete-to' }) as HTMLInputElement;
  const delStatus = h('p', { class: 'form-status', role: 'status', 'data-field': 'delete-status' });
  const delLabel = 'Delete…';
  const delBtn = h('button', { type: 'button', class: 'btn-danger', 'data-action': 'delete-range' }, delLabel) as HTMLButtonElement;
  let armed: { stream: DataStream; from: number; to: number; count: number } | null = null;
  let disarm: ReturnType<typeof setTimeout> | null = null;
  const reset = () => {
    armed = null;
    delBtn.textContent = delLabel;
    if (disarm) clearTimeout(disarm);
  };
  for (const el of [delStream, delFrom, delTo]) el.addEventListener('change', reset);
  delBtn.addEventListener('click', async () => {
    const stream = delStream.value as DataStream;
    const r = dayRange(delFrom.value, delTo.value);
    delStatus.className = 'form-status';
    if (!r) {
      delStatus.className = 'form-status bad';
      delStatus.textContent = 'Pick the first and the last day (the first not after the last).';
      return;
    }
    delBtn.disabled = true;
    try {
      if (!armed || armed.stream !== stream || armed.from !== r.from || armed.to !== r.to) {
        // First click: how many would go — then ask once more.
        const c = await server<{ count: number }>('GET', `/api/me/data/count?stream=${stream}&from=${r.from}&to=${r.to}`);
        if (!c.count) {
          delStatus.textContent = `Nothing of your ${DATA_STREAM_NAMES[stream].toLowerCase()} is kept from ${delFrom.value} to ${delTo.value}.`;
          reset();
        } else {
          armed = { stream, ...r, count: c.count };
          delBtn.textContent = `Click again to delete ${num(c.count)}`;
          delStatus.textContent = `${num(c.count)} kept from ${delFrom.value} to ${delTo.value} (Torn time) will be deleted from the Omni-Torn server for good.`;
          disarm = setTimeout(reset, 8000);
        }
      } else {
        const d = await server<{ deleted: number }>('POST', '/api/me/data/delete', { stream, from: r.from, to: r.to });
        reset();
        delStatus.className = 'form-status ok';
        delStatus.textContent = `Deleted ${num(d.deleted)} — ${DATA_STREAM_NAMES[stream].toLowerCase()} from ${delFrom.value} to ${delTo.value}.`;
        void loadKept();
      }
    } catch (err) {
      reset();
      delStatus.className = 'form-status bad';
      delStatus.textContent = (err as Error).message;
    }
    delBtn.disabled = false;
  });

  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Account & sync'), accountTabs('data')),
    box('What Omni-Torn keeps for you', [
      h('p', { class: 'muted' }, 'Your own history on the Omni-Torn server — only you can see it. Your settings, lists and notes (alert rules among them) and your pricelist are kept too. What you saw of other players and what you shared with your faction aren’t yours alone, so they aren’t part of your backup.'),
      keptHolder,
    ], { id: 'data-kept' }),
    box('Download a backup', [
      h('p', {}, 'One file with everything Omni-Torn keeps for you: your activity log, fights, races, stats history and company days, your settings, lists and notes, alert rules and pricelist. Never a key. It’s made as you download it, so a long history works too (a compressed .ndjson.gz file — one line of JSON each).'),
      h('p', {}, backupBtn),
      backupStatus,
    ], { id: 'data-backup' }),
    box('Restore from a backup', [
      h('p', {}, 'Pick a backup file of yours. It’s checked first — only your own data, in Torn’s own shapes — and you see what would change before anything is sent. History only fills what isn’t kept here; your settings, lists and pricelist are joined with what’s here, and where they differ, what’s here stays unless you choose the backup’s.'),
      h('div', { class: 'data-row' }, h('label', {}, 'Backup file', fileInput)),
      restoreStatus,
      preview,
    ], { id: 'data-restore' }),
    box('Delete kept history by range', [
      h('p', {}, 'Delete part of your history from the Omni-Torn server — one kind at a time, the days you pick (Torn time, both days included). It asks once more before deleting. Omni-Torn won’t read that stretch from Torn again by itself; a backup can put it back. Copies the log pages keep in this browser stay until you use their “Forget”. To delete everything at once: Account → “Delete my data”.'),
      h('div', { class: 'data-row' }, h('label', {}, 'What', delStream), h('label', {}, 'From', delFrom), h('label', {}, 'To', delTo), delBtn),
      delStatus,
    ], { id: 'data-delete' }),
  );
  await loadKept();
}
